import { isModuleHidden } from '@/constants/modules';
import { useEffect, useMemo } from 'react';
import { createBrowserRouter, Outlet, Navigate } from 'react-router';
// React Router 7: the DOM RouterProvider (uses react-dom's flushSync) lives at react-router/dom.
import { RouterProvider } from 'react-router/dom';
import { ShellLayout } from '@/components/layout/ShellLayout';
import { RopGuidedTours } from '@/tours/RopGuidedTours';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { NavGuardModal } from '@/components/common/NavGuardModal';
import { PopOutGate } from '@/components/layout/PopOutGate';
import { ConnectionStatusToast } from '@/components/layout/ConnectionStatusToast';
import { useAuth } from '@/contexts/AuthContext';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useConfig } from '@/contexts/ConfigContext';
import { useClientErrorCapture } from '@/hooks/useClientErrorCapture';
import { RoleLanding } from '@/routes/RoleLanding';
import { Help } from '@/modules/Help';
import { installCloseGuard } from '@/services/teamsMultiWindow';
import pcbLogoUrl from '@/assets/phelps-co-bank-logo-white.svg';
import '@/styles/auth-gate.css';

// ROP OfficerDashboard retired (Bill 2026-07-13 Items 2/5 — raw event feed +
// wonky KPIs); /rop deep-links redirect to the HomeLanding at '/'.
import RLQ from '@/modules/RLQ';
import RAO from '@/modules/RAO';
import RAM from '@/modules/RAM';
import RCM from '@/modules/RCM';
import RIM from '@/modules/RIM';
import { RcdRoutes } from '@/modules/RCD/routes';
import { RsmRoutes } from '@/modules/RSM/routes';
import { RtmRoutes } from '@/modules/RTM/routes';
import { RscRoutes } from '@/modules/RSC/routes';
import RCC from '@/modules/RCC';
import RDL from '@/modules/RDL';
import RKL from '@/modules/RKL';
import RAP from '@/modules/RAP';
import RAS from '@/modules/RAS';
import RAE from '@/modules/RAE';
import RPM from '@/modules/RPM';
import RRM from '@/modules/RRM';
import RNC from '@/modules/RNC';
import RCP from '@/modules/RCP';
import Compliance from '@/modules/Compliance';
import RecordsManagementModule from '@/modules/RecordsManagement';
// WEEKLY-TASKS-BUILD — replaces SP-hub Excel report (Bill 2026-04-25).
import Tasks from '@/modules/Tasks';
import PilotModule from '@/modules/Pilot';
// Q9 (Bill 2026-05-10) — customer-facing signing-completion redirect target.
import { SigningThanks } from '@/modules/RSC/SigningThanks';
// Phase 1 (Bill 2026-05-10) — customer-facing mobile-fallback for drawn
// signatures. Reached via the tokenized QR / link rendered by the desktop
// SigningSurfaceDispatcher. Same pre-auth pattern as SigningThanks.
import { MobileSignRoute } from '@/modules/SignMobile';

/**
 * Root layout for the data router. Hosts the shell chrome, ErrorBoundary,
 * config-loading banner, NavGuardModal (requires data-router context for
 * useBlocker), and an <Outlet /> where per-route modules render.
 */
function RootLayout(): JSX.Element {
  const { state: configState } = useConfig();
  const showConfigBanner = configState === 'initializing' || configState === 'loading';

  return (
    <RopGuidedTours ready={!showConfigBanner}>
    <ShellLayout>
      <ErrorBoundary>
        {showConfigBanner && (
          <div role="status" className="app-shell-config-banner">
            PCB Retail is initializing — config is being loaded from the Function.
            Module surfaces will populate as data becomes available.
          </div>
        )}
        <NavGuardModal />
        <Outlet />
        {/* Wave 1 (Bill 2026-04-28) — surfaces the api-client's
            transparent retries on transient 5xx ("Reconnecting to Core
            Director…") so FSRs aren't staring at a frozen UI during
            cold-start auth-failed retries. Auto-dismisses after 6s. */}
        <ConnectionStatusToast />
      </ErrorBoundary>
    </ShellLayout>
    </RopGuidedTours>
  );
}

/**
 * createBrowserRouter is called once per module evaluation. The router object
 * is memoized at module scope so StrictMode remounts don't rebuild it and
 * wipe history state. Child modules keep their own internal `<Routes>` —
 * the `path: '/rop/*'` splat just hands the subtree off to them.
 */
const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: '/', element: <RoleLanding /> },
      // PILOT 2026-09-11 (Bill): the in-app admin tree is CLOSED. AdminShell
      // hosted a full operations surface inside ROP-teams — Reporting, Kill
      // List, Audit, Notifications, Records Mgmt — which is a standing-rule
      // violation on its own ("ALL admin/config UI goes to the admin-panel
      // SWA, never ROP-teams") and duplicates what admin-panel already ships.
      // It was reachable from an "ADMIN >" link on the Home System Health
      // tile, so a pilot FSR was two clicks from a destructive-cleanup queue.
      // Redirected rather than deleted: the components still need migrating to
      // admin-panel, and deleting them here would lose that work. Restore the
      // route only if that migration is abandoned.
      { path: '/admin/*', element: <Navigate to="/" replace /> },
      { path: '/rop/*', element: <Navigate to="/" replace /> },
      { path: '/rlq/*', element: <RLQ /> },
      { path: '/rao/*', element: <RAO /> },
      // Held back for the pilot: the address goes Home while RAM is hidden (constants/modules.ts).
      { path: '/ram/*', element: isModuleHidden('RAM') ? <Navigate to="/" replace /> : <RAM /> },
      { path: '/rcm/*', element: <RCM /> },
      { path: '/rim/*', element: <RIM /> },
      { path: '/rcd/*', element: <RcdRoutes /> },
      { path: '/rsm/*', element: <RsmRoutes /> },
      { path: '/rtm/*', element: <RtmRoutes /> },
      { path: '/rsc/*', element: <RscRoutes /> },
      { path: '/rcc/*', element: <RCC /> },
      { path: '/rdl/*', element: <RDL /> },
      { path: '/rkl/*', element: <RKL /> },
      { path: '/rap/*', element: <RAP /> },
      { path: '/ras/*', element: <RAS /> },
      { path: '/rae/*', element: <RAE /> },
      { path: '/rpm/*', element: <RPM /> },
      { path: '/rrm/*', element: <RRM /> },
      { path: '/rnc/*', element: <RNC /> },
      { path: '/rcp/*', element: <RCP /> },
      { path: '/compliance/*', element: <Compliance /> },
      // Q9 (Bill 2026-05-10) — customer-facing signing thank-you page.
      // Also short-circuited in App() before auth, but registered here so
      // authenticated FSRs deep-linking to the URL see the same page.
      { path: '/signing-thanks', element: <SigningThanks /> },
      // Phase 1 (Bill 2026-05-10) — customer-facing mobile-fallback for drawn
      // signatures. Pre-auth dispatch in App() handles the no-Teams-session
      // case; this route entry covers FSRs hitting it from inside Teams.
      { path: '/sign-mobile', element: <MobileSignRoute /> },
      // WEEKLY-TASKS-BUILD — Tasks module (Weekly tab today, more later).
      // PILOT 2026-09-11 (Bill): Help module — orientation for pilot users plus
      // the About panel they should screenshot into any support request.
      { path: '/help/*', element: <Help /> },
      { path: '/tasks/*', element: <Tasks /> },
      // 0.162.0 (Bill 2026-11-04): Pilot & requests — pilot testing and the life-ring reporters' dashboard.
      { path: '/pilot/*', element: <PilotModule /> },
      // Wave 6.3 — SuperAdmin Records Management. Role gate enforced
      // inside RecordsManagementShell.
      { path: '/super-admin/records/*', element: <RecordsManagementModule /> },
      { path: '*', element: <RoleLanding /> },
    ],
  },
]);

/**
 * Q9 (Bill 2026-05-10) — customer-facing signing-completion redirect target.
 * SignNow posts customers to `/signing-thanks?cartId=…` after signing; these
 * users have no Teams session and shouldn't be funneled through the auth
 * gate. We dispatch BEFORE the auth-gated `AppAuthenticated` runs so
 * customers see the branded thank-you page without redirect loops.
 *
 * Phase 1 (Bill 2026-05-10) — same pattern for `/sign-mobile?token=…`. The
 * QR + tokenized link rendered by the desktop SigningSurfaceDispatcher
 * targets this path; customers landing here have no Teams SSO.
 */
function isPublicSigningThanksPath(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.toLowerCase().startsWith('/signing-thanks');
}

function isPublicMobileSignPath(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.toLowerCase().startsWith('/sign-mobile');
}

/**
 * BrandedAuthGate (Bill 2026-06-03) — the two browser-direct landing pages.
 * PCB-branded (brand-blue field, white logo), CDN-free.
 *
 *   'use-teams'      → an AUTHENTICATED person opened the SWA in a browser;
 *                      direct them to the Microsoft Teams app.
 *   'not-authorized' → an UNAUTHENTICATED visitor; show a generic refusal that
 *                      reveals NOTHING about what the site is or does.
 */
function BrandedAuthGate({ variant }: { variant: 'use-teams' | 'not-authorized' }): JSX.Element {
  const useTeams = variant === 'use-teams';
  return (
    <div className="pcb-authgate">
      <div className="pcb-authgate__card">
        <img src={pcbLogoUrl} alt="Phelps County Bank" className="pcb-authgate__logo" />
        {useTeams ? (
          <>
            <h1 className="pcb-authgate__title">Continue in Microsoft Teams</h1>
            <p className="pcb-authgate__body">
              This workspace runs inside Microsoft Teams. Open the Teams desktop or
              mobile app and launch it from there to continue.
            </p>
          </>
        ) : (
          <>
            <h1 className="pcb-authgate__title">Not authorized</h1>
            <p className="pcb-authgate__body">
              You don’t have access to this resource. If you believe this is an
              error, contact your administrator.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export function App(): JSX.Element {
  if (isPublicSigningThanksPath()) {
    return <SigningThanks />;
  }
  if (isPublicMobileSignPath()) {
    return <MobileSignRoute />;
  }
  return <AppAuthenticated />;
}

function AppAuthenticated(): JSX.Element {
  useClientErrorCapture();
  const { loading: authLoading, error: authError, user } = useAuth();
  const apiClient = useApiClient();

  // Best-effort Teams/browser close-guard. SessionContext's
  // registerBeforeUnloadHandler handles the isDirty path; this one handles
  // the activeCartId-only path + the non-Teams browser fallback.
  useEffect(() => {
    if (!user) return;
    const uninstall = installCloseGuard();
    return uninstall;
  }, [user]);

  // Cold-start preheat (Bill 2026-04-28): fire one fire-and-forget /health
  // ping the moment we have an authenticated user + ApiClient, so the JHA
  // ims.jhacloudservices.com DNS cache + Function instance are warm before
  // the FSR's first real action. Silent — no UI surface (the user is
  // reading the home screen). On failure, we don't retry; the next real
  // action will hit the api-client's transparent retry path.
  useEffect(() => {
    if (!user || !apiClient) return;
    let cancelled = false;
    void (async () => {
      try {
        await apiClient.health.ping();
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.debug('[App] Preheat ping completed.');
      } catch {
        // Silent — preheat failures are non-fatal; the next real call will
        // exercise the api-client retry policy.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, apiClient]);

  // Keep the router reference stable across the App's own re-renders.
  const routerEl = useMemo(() => <RouterProvider router={router} />, []);

  if (authLoading) {
    return (
      <div className="app-shell-loading">
        <LoadingSpinner size="lg" label="Signing in…" />
      </div>
    );
  }

  // Browser-direct landings (Bill 2026-06-03). Authenticated → route to Teams;
  // unauthenticated → generic refusal that reveals nothing about the app.
  if (authError === 'USE_TEAMS_APP') {
    return <BrandedAuthGate variant="use-teams" />;
  }
  if (authError === 'NOT_AUTHORIZED') {
    return <BrandedAuthGate variant="not-authorized" />;
  }

  if (authError === 'TEAMS_HOST_REQUIRED') {
    // Legacy state (pre-2026-06-03 browser-direct). Treat as the authenticated
    // "use Teams" landing — this only fires for an in-Teams host that bailed.
    return <BrandedAuthGate variant="use-teams" />;
  }

  if (authError === 'TEAMS_WEB_NOT_SUPPORTED') {
    return (
      <div className="auth-gate">
        <div className="auth-gate__card">
          <h1 className="auth-gate__title">Please use Teams desktop or mobile</h1>
          <p className="auth-gate__body">
            This application is not available in the browser version of Microsoft Teams.
            Open Microsoft Teams on your desktop or mobile device and launch Retail from there.
          </p>
        </div>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="auth-gate--error">
        <h1>Access restricted</h1>
        <p>Unable to complete sign-in.</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="app-shell-session-required">
        <h1>Access restricted</h1>
      </div>
    );
  }

  // PopOutGate intercepts launches in the Teams desktop MAIN window and shows
  // a landing screen asking the officer to pop out. Every other host
  // (desktop-popout / web / mobile / dev browser) passes through to the
  // router immediately — see services/teamsMultiWindow.detectHostEnvironment.
  return <PopOutGate>{routerEl}</PopOutGate>;
}
